import { HourglassTopRounded, ScheduleRounded } from "@mui/icons-material";
import { Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime, Settings } from "luxon";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK, RADIUS } from "../../theme/tokens";
import { PulseDot } from "../UIToolkit/Animations/PulseDot";

// setTimeout treats a delay above 2^31-1 ms (~24.8 days) as 0 and fires at once, so a longer wait re-arms in steps.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// One line-height for every row, so the leading glyph's box can centre on the FIRST line when the text wraps.
const ROW_LINE_HEIGHT = 1.45;

const END_TIME_FORMAT: Intl.DateTimeFormatOptions = {
	weekday: "short",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "2-digit",
	timeZoneName: "short",
};

// Staff at a US facility know their zone by its US abbreviation ("EDT"). Hebrew has no short names for US zones, so
// Intl prints "GMT-4" there, which reads as a different time; the zone name is always taken from US English. Outside
// the US zones, US English gives the same "GMT+3" any locale would, so nothing is lost.
const ZONE_NAME_LOCALE = "en-US";

const NO_BREAK_SPACE = "\u00A0";

/**
 * "Sun, Oct 4, 2:00 AM EDT" in the device's zone and the UI language (Settings.defaultLocale follows i18n). One Intl
 * format, so each language supplies its own order and connectors ("dom, 4 de oct, 2:00 a.m. EDT").
 */
const formatEndsAt = (endsAt: DateTime): string => {
	const local = endsAt.toLocal();
	const zoneName = local.setLocale(ZONE_NAME_LOCALE).toFormat("ZZZZ");
	const parts = local.setLocale(Settings.defaultLocale).toLocaleParts(END_TIME_FORMAT);
	const timeStart = parts.findIndex((part) => part.type === "hour");
	return parts
		.map((part, index) => {
			if (part.type === "timeZoneName") return zoneName;
			// Keep "9:00 a.m. EDT" on one line: a wrap inside the time strands "a.m." or the zone on the next row.
			return index > timeStart && part.type === "literal" ? part.value.replace(/ /g, NO_BREAK_SPACE) : part.value;
		})
		.join("");
};

/** Whether `endsAt` has passed — re-evaluated when it does, so a screen left open flips to "taking longer" on time. */
const useHasPassed = (endsAt: DateTime | null): boolean => {
	const [now, setNow] = useState(() => DateTime.now());
	useEffect(() => {
		if (!endsAt) return;
		const remaining = endsAt.diff(now).toMillis();
		if (remaining <= 0) return;
		const timer = window.setTimeout(() => setNow(DateTime.now()), Math.min(remaining, MAX_TIMEOUT_MS));
		return () => window.clearTimeout(timer);
	}, [endsAt, now]);
	return endsAt !== null && endsAt <= now;
};

const ROW_SX: SxProps<Theme> = { display: "flex", alignItems: "flex-start", gap: 1.5, px: 2, py: 1.5 };

// A glyph column one icon wide and one text line tall: the clock and the dot share it, so every row's text starts on one edge.
const LEADING_SX: SxProps<Theme> = {
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
	width: ICON_SIZE.INLINE,
	fontSize: FONT_SIZE.EMPHASIS,
	height: `${ROW_LINE_HEIGHT}em`,
	"& > svg": { fontSize: ICON_SIZE.INLINE },
};

const rowText = (children: ReactNode, color: string) => (
	<Typography component="p" sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: ROW_LINE_HEIGHT, color, textWrap: "pretty" }}>
		{children}
	</Typography>
);

type Props = {
	/** The planned end time; null when the notice gave no (valid) time. */
	endsAt: DateTime | null;
	/** Already-translated "this page reloads by itself" line. */
	autoReload: string;
};

/** What happens next: when we expect to be back (planned), then the always-on "this page keeps checking" line. */
export const MaintenanceStatusPanel: FC<Props> = ({ endsAt, autoReload }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const hasPassed = useHasPassed(endsAt);
	const brand = theme.palette.primary.main;

	return (
		<Box
			sx={{
				width: "100%",
				textAlign: "start",
				borderRadius: `${RADIUS.CONTROL}px`,
				backgroundColor: alpha(brand, ALPHA.HINT_TINT),
				"& > * + *": { borderTop: `1px solid ${alpha(brand, ALPHA.CONTROL_TINT_HOVER)}` },
			}}
		>
			{endsAt && !hasPassed && (
				<Box data-testid="maintenance-screen-ends-at" sx={ROW_SX}>
					<Box component="span" sx={[LEADING_SX, { color: brand }]}>
						<ScheduleRounded aria-hidden />
					</Box>
					{rowText(
						<Trans
							// Passing `t` makes the element depend on the language; without it the React Compiler memoises it in the first one.
							t={t}
							i18nKey="maintenance.planned.ends.at"
							values={{ time: formatEndsAt(endsAt) }}
							components={{ b: <Box component="strong" sx={{ fontWeight: 700 }} /> }}
						/>,
						INK.PRIMARY,
					)}
				</Box>
			)}
			{endsAt && hasPassed && (
				<Box data-testid="maintenance-screen-overdue" sx={ROW_SX}>
					<Box component="span" sx={[LEADING_SX, { color: theme.palette.warning.main }]}>
						<HourglassTopRounded aria-hidden />
					</Box>
					{rowText(t("maintenance.planned.overdue"), INK.PRIMARY)}
				</Box>
			)}
			<Box role="status" aria-live="polite" data-testid="maintenance-screen-status" sx={ROW_SX}>
				<Box component="span" sx={LEADING_SX}>
					<PulseDot />
				</Box>
				{rowText(autoReload, INK.SECONDARY)}
			</Box>
		</Box>
	);
};
